// src/landing/Suporte.jsx

import React, { useMemo, useState, useCallback } from "react";
import { Container, Row, Col, Offcanvas } from "react-bootstrap";
import { FaWhatsapp, FaEnvelope, FaSearch } from "react-icons/fa";
import Style from '../styles/landing/Suporte.module.css';
import ArtAvaliacaoCorporal    from "./components/ArtAvaliacaoCorporal";
import ArtPlanoNutriComple     from "./components/ArtPlanoNutriComple";
import ArtVerResultAval        from "./components/ArtVerResultAval";
import ArtSubstituiExameMedico from "./components/ArtSubstituiExameMedico";
import { useNavigate } from "react-router-dom";
import { useEdgeSwipeClose } from "../hooks/useEdgeSwipeClose";
import {
  abrirWhatsapp,
  buildWhatsappWebUrl,
  getSuporteWhatsappNumero,
} from "../utils/whatsappLink";

/** Mensagem que já chega escrita: chegar com o cursor piscando faz fechar. */
const MENSAGEM_SUPORTE = "Olá! Preciso de ajuda com o app LesVita.";

/** `5511939537302` → `+55 (11) 93953-7302`, só para exibir. */
function formatarNumeroBR(digitos) {
  const m = /^55(\d{2})(\d{4,5})(\d{4})$/.exec(digitos || "");
  return m ? `+55 (${m[1]}) ${m[2]}-${m[3]}` : `+${digitos}`;
}

// ─── Registry de artigos ──────────────────────────────────────────────────────
const ARTICLE_REGISTRY = {
  "como-avaliacao-corporal": {
    id: "como-avaliacao-corporal",
    title: "Como faço minha avaliação corporal?",
    Component: ArtAvaliacaoCorporal,
  },
  "como-plano-nutricional": {
    id: "como-plano-nutricional",
    title: "Como faço o plano nutricional completo?",
    Component: ArtPlanoNutriComple,
  },
  "como-ver-resultado-avaliacao": {
    id: "como-ver-resultado-avaliacao",
    title: "Como ver o resultado da avaliação?",
    Component: ArtVerResultAval,
  },
  "avaliacao-substitui-exame-medico": {
    id: "avaliacao-substitui-exame-medico",
    title: "Avaliação corporal substitui um exame médico?",
    Component: ArtSubstituiExameMedico,
  },
};

const SUPPORT_SECTIONS = [
  {
    id: "primeiros-passos",
    title: "Primeiros passos",
    articles: ["como-avaliacao-corporal", "como-plano-nutricional"],
  },
  {
    id: "composicao-corporal",
    title: "Composição corporal",
    articles: [
      "como-avaliacao-corporal",
      "como-ver-resultado-avaliacao",
      "avaliacao-substitui-exame-medico",
    ],
  },
];

// ─── SVG reutilizável ─────────────────────────────────────────────────────────
const BackArrow = ({ className }) => (
  <svg width="31" height="19" viewBox="0 0 31 19" fill="none"
    xmlns="http://www.w3.org/2000/svg" className={className}>
    <path
      d="M17.1799 17.6508L30.1086 4.66082C30.3231 4.44747 30.4932 4.19375 30.6091 3.91432C30.725 3.63489 30.7845 3.33526 30.784 3.03274C30.7836 2.73021 30.7232 2.43077 30.6065 2.15168C30.4897 1.87259 30.3189 1.61938 30.1038 1.40666C29.6738 0.980473 29.0926 0.74176 28.4871 0.742658C27.8817 0.743555 27.3012 0.98399 26.8725 1.41145L15.431 12.7722L4.07045 1.44525C3.64045 1.01907 3.05927 0.780353 2.45384 0.78125C1.84842 0.782148 1.26795 1.02258 0.839207 1.45004C0.622987 1.66261 0.451097 1.91597 0.333498 2.19544C0.215899 2.47491 0.154931 2.77494 0.154122 3.07815C0.15583 3.38135 0.217687 3.68119 0.336114 3.96032C0.454541 4.23944 0.627181 4.49229 0.844031 4.70421L13.8112 17.6558C14.0261 17.8881 14.2868 18.0734 14.5769 18.1999C14.867 18.3265 15.1801 18.3916 15.4966 18.3911C15.8131 18.3906 16.1261 18.3246 16.4158 18.1972C16.7055 18.0698 16.9657 17.8838 17.1799 17.6508Z"
      fill="currentColor"
    />
  </svg>
);

// ─── Componente principal ─────────────────────────────────────────────────────
const Suporte = ({ closeSuport, hrefPage }) => {
  const [searchTerm,       setSearchTerm]       = useState("");
  const [selectedArticleId, setSelectedArticleId] = useState(null);
  const [showOffcanvas,    setShowOffcanvas]    = useState(false);
  const navigate = useNavigate();
  // Número do suporte: env, nunca hardcoded. Sem env, o card não aparece.
  const suporteWhatsapp = getSuporteWhatsappNumero();

  // Artigo offcanvas
  const handleOpenArticle  = (articleId) => { setSelectedArticleId(articleId); setShowOffcanvas(true); };
  const handleCloseArticle = useCallback(() => { setShowOffcanvas(false); setSelectedArticleId(null); }, []);

  // Voltar / fechar Suporte — chama o wrapper que reabre Config se necessário
  const handleClose = useCallback(() => {
    if (closeSuport) closeSuport(); // setActiveSectionWrapped(null) vindo do pai
    else navigate("/");
  }, [closeSuport, navigate]);

  // Swipe: fecha artigo se aberto, senão fecha Suporte
  const closeBySwipe = useCallback(() => {
    if (showOffcanvas) { handleCloseArticle(); return; }
    handleClose();
  }, [showOffcanvas, handleCloseArticle, handleClose]);

  useEdgeSwipeClose({
    enabled: true,
    onClose: closeBySwipe,
    side: "right",
    edge: 40,
    minDist: 45,
  });

  // Busca
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredSections = useMemo(() => {
    if (!normalizedSearch) return SUPPORT_SECTIONS;
    return SUPPORT_SECTIONS.map((section) => ({
      ...section,
      articles: section.articles.filter((articleId) => {
        const article = ARTICLE_REGISTRY[articleId];
        if (!article) return false;
        return (
          article.title.toLowerCase().includes(normalizedSearch) ||
          section.title.toLowerCase().includes(normalizedSearch)
        );
      }),
    })).filter((s) => s.articles.length > 0);
  }, [normalizedSearch]);

  const selectedArticle = selectedArticleId ? ARTICLE_REGISTRY[selectedArticleId] : null;

  return (
    <div className={Style.supportPage}>
      <Container className={Style.supportContainer}>

        {/* Voltar */}
        <div className={Style.containerBack}>
          <a href={hrefPage} onClick={(e) => { e.preventDefault(); handleClose(); }}>
            <BackArrow className={Style.backArrow} />
          </a>
        </div>

        {/* Contatos */}
        <Row className={Style.contactRow}>
          {/* Abre o app do WhatsApp no nativo (AppLauncher) e wa.me fora da
              WebView/na web — ver utils/whatsappLink.js. O número vem de env;
              sem ele o card some em vez de apontar para um número errado. */}
          {suporteWhatsapp && (
            <Col xs={12} md={6}>
              <a
                href={buildWhatsappWebUrl(suporteWhatsapp, MENSAGEM_SUPORTE)}
                onClick={(e) => { e.preventDefault(); abrirWhatsapp({ mensagem: MENSAGEM_SUPORTE }); }}
                target="_blank"
                rel="noopener noreferrer"
                className={Style.contactValue}
              >
                <div className={Style.contactBox}>
                  <div className={Style.contactIcon}><FaWhatsapp /></div>
                  <div className={Style.contactContent}>
                    <span className={Style.contactLabel}>Suporte via WhatsApp</span>
                    {formatarNumeroBR(suporteWhatsapp)}
                  </div>
                </div>
              </a>
            </Col>
          )}
          <Col xs={12} md={6}>
            <a href="mailto:suporte@lesvita.com" className={Style.contactValue}>
              <div className={Style.contactBox}>
                <div className={Style.contactIcon}><FaEnvelope /></div>
                <div className={Style.contactContent}>
                  <span className={Style.contactLabel}>Suporte por e-mail</span>
                  suporte@lesvita.com
                </div>
              </div>
            </a>
          </Col>
        </Row>

        {/* Busca */}
        <div className={Style.searchWrapper}>
          <div className={Style.searchIcon}><FaSearch /></div>
          <input
            type="text"
            placeholder="Buscar artigos de ajuda"
            className={Style.searchInput}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Seções */}
        <div className={Style.sectionsWrapper}>
          {filteredSections.length === 0 && (
            <p className={Style.emptyState}>
              Não encontramos artigos para essa busca. Tente usar outras palavras.
            </p>
          )}
          {filteredSections.map((section) => (
            <div key={section.id} className={Style.sectionBlock}>
              <h2 className={Style.sectionTitle}>{section.title}</h2>
              <ul className={Style.articleList}>
                {section.articles.map((articleId) => {
                  const article = ARTICLE_REGISTRY[articleId];
                  if (!article) return null;
                  return (
                    <li key={article.id} className={Style.articleItem}>
                      <button type="button" className={Style.articleButton}
                        onClick={() => handleOpenArticle(article.id)}>
                        {article.title}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <hr className={Style.sectionDivider} />
            </div>
          ))}
        </div>
      </Container>

      {/* Offcanvas de artigo */}
      <Offcanvas
        show={showOffcanvas}
        onHide={handleCloseArticle}
        placement="end"
        className={Style.articleOffcanvas}
      >
        <Offcanvas.Header>
          <button onClick={handleCloseArticle} className={Style.btnsHeaderOff}>
            <BackArrow className={Style.backArrow} />
          </button>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {selectedArticle && (
            <selectedArticle.Component title={selectedArticle.title} />
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default Suporte;